import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
  equipmentIntakeService,
  OWNERSHIP_TYPE_LABELS,
  type AddressSelection,
  type IntakeAccessoryStatus,
  type IntakeOwnershipType,
} from '@/services/equipment-intake.service';
import { priorityService } from '@/services/priority.service';
import { type Customer } from '@/services/customer.service';
import { EquipmentSearchCombobox } from './EquipmentSearchCombobox';
import { CustomerSearchSelect } from './CustomerSearchSelect';
import { AddressFormModal, type AddressFormMode } from './components/AddressFormModal';
import { ContactFormModal, type ContactFormMode } from './components/ContactFormModal';
import { ContactPicker } from './components/ContactPicker';
import { AddContactPrompt } from './components/AddContactPrompt';
import { CustomerFormModal } from '@/features/customers/components/CustomerFormModal';
import { AccessoryChecklist, type AccessoryRow } from './components/AccessoryChecklist';
import { PhotoUploadSection, type PhotoEntry } from './components/PhotoUploadSection';
import { EngineerSelect } from './components/EngineerSelect';
import { ShipmentCard, type ShipmentState } from './components/ShipmentCard';
import { AddressSection } from './components/AddressSection';
import {
  ReceivedConditionChecklist,
  fromReceivedCondition,
  toReceivedConditionPayload,
  type ReceivedConditionState,
} from './components/ReceivedConditionChecklist';
import { ReceivedLocationChangePrompt } from './components/ReceivedLocationChangePrompt';
import { OWNERSHIP_TYPES, SELECT_CLASS, MAX_PHOTOS } from './intake-form.constants';
import {
  useIntakeCustomer,
  useIntakeCustomerContacts,
  useIntakeCustomerLocations,
  useIntakeEquipmentModel,
  useIntakeInspection,
  firstInspection,
} from './intake-queries';
import {
  modeFlags,
  toAccessoryInputs,
  toShipmentPayloads,
  validateIntakeForm,
  type IntakeFileState,
  type IntakeFormData,
  type IntakeFormInitial,
  type IntakeFormMode,
} from './intake-form.state';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { useHasPermission } from '@/hooks/use-permissions';

function extractApiMessage(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

/**
 * The one intake form.
 *
 * Modes change BEHAVIOUR ONLY — which fields accept input and where a value is
 * written. They never change layout: the same sections appear in the same order
 * whether the intake is being created, edited or read, because a form whose
 * shape depends on its mode is two forms with one name.
 *
 *   create    everything writable, POSTs a new intake
 *   edit      create-only fields locked, PATCHes an existing intake
 *   readonly  every field disabled, no submit
 *
 * OWNERSHIP (Phase 4A, approved):
 *   Engineer, priority and warranty belong to the INSPECTION. This form reads
 *   and writes them through the inspection endpoints and never through the
 *   intake payload. EquipmentIntake.assignedEngineerId is legacy compatibility
 *   data and is not written here.
 *
 *   customerId, equipmentId, intakeDate and ownershipTypeAtIntake are
 *   CREATE-ONLY. The server enforces this structurally — they are absent from
 *   UpdateEquipmentIntakeDto — and this form simply agrees with it.
 *
 *   receivedConditionJson is the authoritative condition record.
 *   conditionOnReceipt is legacy free text: displayed read-only on an existing
 *   intake, never written back.
 *
 * Bill-To / Ship-To never travel through this form's save. They are captured at
 * creation and afterwards change only through the dedicated address endpoint.
 */
export function IntakeForm({
  mode,
  initial,
  onSubmit,
  onCancel,
  saving,
}: {
  mode: IntakeFormMode;
  initial?: IntakeFormInitial;
  onSubmit: (data: IntakeFormData, fileState: IntakeFileState) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const { isCreate, canEdit } = modeFlags(mode);
  // Read-only disables every control without changing a single element, so the
  // form reads identically whether or not it can be written.
  const locked = saving || !canEdit;
  const can = useHasPermission();

  const [intakeDate, setIntakeDate] = useState(
    initial?.intakeDate ? initial.intakeDate.slice(0, 10) : today,
  );
  const [customerId, setCustomerId] = useState(initial?.customerId ?? '');
  const [customerLocationId, setCustomerLocationId] = useState(initial?.customerLocationId ?? '');
  const [customerContactId, setCustomerContactId] = useState(initial?.customerContactId ?? '');

  // ── Bill-To / Ship-To selections ────────────────────────────────────────────
  // Defaults derived from the three locked business cases:
  //   Bill-To  -> customer default (Infosys, Wipro; Tata overrides in one click)
  //   Ship-To  -> the selected location (all three cases)
  // These are SELECTIONS only. The server resolves and freezes the addresses.
  // On an existing intake the stored snapshots are immutable and are edited
  // through the dedicated endpoint, never by submitting this form.
  const [billTo, setBillTo] = useState<AddressSelection>({ source: 'CUSTOMER_DEFAULT' });
  const [shipTo, setShipTo] = useState<AddressSelection>(
    initial?.customerLocationId
      ? { source: 'LOCATION', locationId: initial.customerLocationId }
      : { source: 'CUSTOMER_DEFAULT' },
  );
  const [equipmentId, setEquipmentId] = useState(initial?.equipmentId ?? '');
  const [ownershipTypeAtIntake, setOwnershipTypeAtIntake] = useState<IntakeOwnershipType | ''>(
    isCreate ? '' : (initial?.ownershipTypeAtIntake ?? ''),
  );
  const [complaintDescription, setComplaintDescription] = useState(
    initial?.complaintDescription ?? '',
  );
  // Legacy free text — display only. There is no setter: this form never
  // writes it back (Step 4).
  const conditionOnReceipt = initial?.conditionOnReceipt ?? '';
  // Structured checklist. An intake that never recorded one — every bulk-created
  // intake, and anything predating the feature — opens with all six items
  // present and unset, ready to fill in. That is what makes a bulk intake
  // completable here rather than permanently missing data.
  const [receivedCondition, setReceivedCondition] = useState<ReceivedConditionState>(() =>
    fromReceivedCondition(initial?.receivedConditionJson),
  );
  const [remarks, setRemarks] = useState(initial?.remarks ?? '');
  // STEP 2 — the INSPECTION owns engineer assignment. The intake column is
  // legacy compatibility data: it seeds the control only until the inspection
  // resolves, and is never written back by this form.
  const [assignedEngineerId, setAssignedEngineerId] = useState(
    initial?.legacyAssignedEngineerId ?? '',
  );
  const engineerInitialised = useRef(false);

  // ── Priority — an INSPECTION field ──────────────────────────────────────────
  // Read from and written to the inspection, never to EquipmentIntake. The
  // intake carries no priority column and must not grow one: the same machine
  // can be re-inspected at a different urgency without its intake changing.
  const canUpdateInspection = can('inspection:update');
  const canReadInspection = can('inspection:read');
  const { data: inspectionList } = useIntakeInspection(
    initial?.intakeId,
    !isCreate && canReadInspection,
  );
  const inspection = firstInspection(inspectionList);
  const { data: priorities } = useQuery({
    queryKey: ['priorities-all'],
    queryFn: () => priorityService.listAll(),
    // Only fetch the options when there is a control to put them in.
    enabled: canUpdateInspection,
  });
  const [priorityId, setPriorityId] = useState('');
  // The inspection resolves after first render; adopt its priority once, and
  // never afterwards, so it cannot stomp an edit in progress.
  const priorityInitialised = useRef(false);
  useEffect(() => {
    if (priorityInitialised.current || !inspection) return;
    priorityInitialised.current = true;
    setPriorityId(inspection.priorityId ?? '');
    if (!engineerInitialised.current) {
      engineerInitialised.current = true;
      setAssignedEngineerId(inspection.assignedEngineerId ?? '');
    }
  }, [inspection]);

  // ── Received-location change → SUGGEST a Ship-To update, never apply one ────
  const [locationPrompt, setLocationPrompt] = useState<{ locationId: string } | null>(null);

  // Ship-To ONLY, through the dedicated endpoint. `billTo` is deliberately not
  // in this payload: a change of receiving location must never be able to move
  // where the customer is invoiced.
  const shipToUpdate = useMutation({
    mutationFn: ({ intakeId, locationId }: { intakeId: string; locationId: string }) =>
      equipmentIntakeService.updateAddresses(intakeId, {
        shipTo: { source: 'LOCATION', locationId },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['equipment-intake'] });
      void queryClient.invalidateQueries({ queryKey: ['equipment-intake-detail'] });
      setLocationPrompt(null);
      toast.success('Ship-To updated');
    },
    onError: (error: Error) => {
      toast.error(extractApiMessage(error, 'Failed to update Ship-To'));
      setLocationPrompt(null);
    },
  });

  const [accessoryRows, setAccessoryRows] = useState<AccessoryRow[]>(() =>
    (initial?.accessories ?? []).map((a) => ({
      accessoryId: a.accessoryId ?? null,
      accessoryName: a.accessoryName,
      status: a.status,
      notes: a.notes ?? '',
    })),
  );

  const [photoRemovedIndices, setPhotoRemovedIndices] = useState<number[]>([]);
  const [pendingPhotoEntries, setPendingPhotoEntries] = useState<PhotoEntry[]>([]);

  const [shipmentStates, setShipmentStates] = useState<ShipmentState[]>(() =>
    (initial?.shipments ?? []).map((s) => ({
      id: s.id,
      shipmentType: s.shipmentType,
      courierName: s.courierName ?? '',
      trackingNumber: s.trackingNumber ?? '',
      dispatchDate: s.dispatchDate ? s.dispatchDate.slice(0, 10) : '',
      receivedDate: s.receivedDate ? s.receivedDate.slice(0, 10) : '',
      remarks: s.remarks ?? '',
      existingAttachments: s.attachments,
      removedAttachmentIndices: [],
      pendingAttachmentFiles: [],
    })),
  );

  // Object URL cleanup
  const pendingPhotoRef = useRef<PhotoEntry[]>([]);
  useEffect(() => {
    pendingPhotoRef.current = pendingPhotoEntries;
  }, [pendingPhotoEntries]);
  useEffect(() => {
    return () => {
      pendingPhotoRef.current.forEach((e) => URL.revokeObjectURL(e.previewUrl));
    };
  }, []);

  const queryClient = useQueryClient();

  // Search-first customer selection (Phase 1). The selected customer object is
  // held locally so its label renders without a preloaded list; in edit mode we
  // resolve it by id.
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [locationModal, setLocationModal] = useState<AddressFormMode | null>(null);
  const [contactModal, setContactModal] = useState<ContactFormMode | null>(null);
  const [promptContactFor, setPromptContactFor] = useState<string | null>(null);

  const { data: resolvedCustomer } = useIntakeCustomer(customerId, !selectedCustomer);
  const effectiveCustomer = selectedCustomer ?? resolvedCustomer ?? null;

  const { data: customerLocations } = useIntakeCustomerLocations(customerId);

  // Contacts belong to a location, so they are scoped to the selected location
  // and reload whenever the location changes.
  const { data: customerContacts } = useIntakeCustomerContacts(customerId, customerLocationId);

  // Equipment is chosen via server-side autocomplete (no full list in browser).
  // The selected equipment's model id is tracked here to drive accessory auto-load.
  const [selectedEquipmentModelId, setSelectedEquipmentModelId] = useState<string | null>(null);

  const { data: modelData } = useIntakeEquipmentModel(selectedEquipmentModelId);

  // Reset location/contact whenever the customer changes.
  const handleCustomerSelect = (customer: Customer | null) => {
    setCustomerId(customer?.id ?? '');
    setSelectedCustomer(customer);
    setIsNewCustomer(false);
    setCustomerLocationId('');
    setCustomerContactId('');
  };

  // A customer just created inline. New customers must gain a location and a
  // contact before intake can continue, so open the location step immediately.
  const handleCustomerCreated = (customer: Customer) => {
    setCustomerId(customer.id);
    setSelectedCustomer(customer);
    setIsNewCustomer(true);
    setCustomerLocationId('');
    setCustomerContactId('');
    setCustomerModalOpen(false);
    // Customer → Location → Contact: the chain continues without being asked for.
    setLocationModal({ kind: 'create' });
  };

  // Reset the contact whenever the location changes — contacts are location-scoped.
  const handleLocationChange = (locationId: string) => {
    const previous = customerLocationId;
    setCustomerLocationId(locationId);
    setCustomerContactId('');

    // On an EXISTING intake the Ship-To snapshot is already frozen against the
    // old location. Offer to move it — never move it. Bill-To is not part of
    // this at all: billing is agreed with the customer, not with the loading
    // bay. Nothing is written unless the operator confirms in the prompt.
    if (!isCreate && locationId && previous && locationId !== previous) {
      setLocationPrompt({ locationId });
    }
  };

  // Received-From only. Bill-To / Ship-To own their own Add Address dialog, so
  // creating a branch to bill to can no longer move where the equipment came from.
  const handleLocationCreated = (locationId: string) => {
    void queryClient.invalidateQueries({ queryKey: ['customer-locations', customerId] });
    setPromptContactFor(locationId);

    setCustomerLocationId(locationId);
    setCustomerContactId('');
  };

  const handleContactCreated = (contactId: string) => {
    void queryClient.invalidateQueries({
      queryKey: ['customer-location-contacts', customerId, customerLocationId],
    });
    setCustomerContactId(contactId);
  };

  const lastLoadedModelId = useRef<string | null>(null);
  useEffect(() => {
    if (!isCreate) return;
    if (!selectedEquipmentModelId) {
      setAccessoryRows([]);
      lastLoadedModelId.current = null;
      return;
    }
    if (modelData && modelData.id !== lastLoadedModelId.current) {
      lastLoadedModelId.current = modelData.id;
      setAccessoryRows(
        modelData.accessories.map((a) => ({
          accessoryId: a.id,
          accessoryName: a.name,
          status: 'RECEIVED' as IntakeAccessoryStatus,
          notes: '',
        })),
      );
    }
  }, [isCreate, selectedEquipmentModelId, modelData]);

  const addPhotos = useCallback((files: File[]) => {
    const entries = files.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setPendingPhotoEntries((prev) => [...prev, ...entries]);
  }, []);

  const removePendingPhoto = useCallback(
    (i: number) => {
      URL.revokeObjectURL(pendingPhotoEntries[i]!.previewUrl);
      setPendingPhotoEntries((prev) => prev.filter((_, idx) => idx !== i));
    },
    [pendingPhotoEntries],
  );

  const updateShipment = (idx: number, state: ShipmentState) =>
    setShipmentStates((prev) => prev.map((s, i) => (i === idx ? state : s)));
  const removeShipment = (idx: number) =>
    setShipmentStates((prev) => prev.filter((_, i) => i !== idx));
  const addShipment = () =>
    setShipmentStates((prev) => [
      ...prev,
      {
        shipmentType: 'COURIER',
        courierName: '',
        trackingNumber: '',
        dispatchDate: '',
        receivedDate: '',
        remarks: '',
        existingAttachments: [],
        removedAttachmentIndices: [],
        pendingAttachmentFiles: [],
      },
    ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const problem = validateIntakeForm({
      equipmentId,
      complaintDescription,
      customerLocationId,
      customerContactId,
      isNewCustomer,
    });
    if (problem) {
      // A missing required field is silent (the browser marks it); a business
      // rule violation is announced, exactly as before.
      if (equipmentId && complaintDescription.trim()) toast.error(problem);
      return;
    }

    const accessories = toAccessoryInputs(accessoryRows);

    const shipmentsPayload = toShipmentPayloads(shipmentStates);

    onSubmit(
      {
        intakeDate: new Date(intakeDate).toISOString(),
        customerId,
        customerLocationId,
        customerContactId,
        equipmentId,
        ownershipTypeAtIntake,
        complaintDescription: complaintDescription.trim(),
        receivedConditionJson: toReceivedConditionPayload(receivedCondition),
        priorityId,
        remarks,
        accessories,
        assignedEngineerId,
        shipments: shipmentsPayload,
        // Selections only, and only on create — an existing intake's addresses
        // are immutable here and change solely via updateAddresses().
        ...(isCreate && customerId ? { billTo, shipTo } : {}),
      },
      {
        photoPendingFiles: pendingPhotoEntries.map((e) => e.file),
        photoRemovedIndices,
        shipmentStates,
      },
    );
  };

  const activeLocations = (customerLocations ?? []).filter((l) => !l.deletedAt);
  const activeContacts = (customerContacts ?? []).filter((c) => !c.deletedAt);
  const selectedLocation = activeLocations.find((l) => l.id === customerLocationId) ?? null;

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Inward Date" htmlFor="ii-date" required>
          {/* Read-only once the intake exists: the arrival date is a statement
              of fact about a delivery that already happened, and intake
              numbering, the receiving lot and the inspection all hang off it. */}
          <Input
            id="ii-date"
            type="date"
            value={intakeDate}
            onChange={(e) => setIntakeDate(e.target.value)}
            required
            disabled={locked || !isCreate}
            readOnly={!isCreate}
          />
        </FormField>

        <FormField label="Customer" htmlFor="ii-customer">
          {/* Locked after creation, exactly like Equipment and Intake Date.
              Rendered as a plain disabled input rather than a disabled search
              control: the search control still offered a clear (✕) button and a
              dropdown, so the customer LOOKED changeable while the server —
              which has no customerId on its update DTO — rejected any such
              change. A control that cannot do anything must not be shown as
              though it can. Changing the customer would in any case strand the
              frozen Bill-To snapshot on the previous customer. */}
          {initial ? (
            <>
              <Input id="ii-customer" value={initial.customerLabel} readOnly disabled />
              <p className="mt-1 text-xs text-muted-foreground">Locked after intake creation.</p>
            </>
          ) : (
            <CustomerSearchSelect
              selected={effectiveCustomer}
              onSelect={handleCustomerSelect}
              onCreateNew={() => setCustomerModalOpen(true)}
              disabled={locked}
            />
          )}
        </FormField>

        {customerId && (
          <>
            <FormField label="Received from" htmlFor="ii-location" required={isNewCustomer}>
              <div className="flex items-center gap-2">
                <select
                  id="ii-location"
                  value={customerLocationId}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  disabled={locked}
                  className={SELECT_CLASS}
                >
                  <option value="">
                    {isNewCustomer ? '— Select location —' : '— Select location (optional) —'}
                  </option>
                  {activeLocations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                      {l.isPrimary ? ' ★' : ''} — {l.address}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setLocationModal({ kind: 'create' })}
                  disabled={locked}
                >
                  + Add
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  aria-label="Edit location"
                  title={selectedLocation ? 'Edit this location' : 'Select a location first'}
                  disabled={locked || !selectedLocation}
                  onClick={() =>
                    selectedLocation &&
                    setLocationModal({ kind: 'edit-place', location: selectedLocation })
                  }
                >
                  <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                </Button>
              </div>
            </FormField>

            <FormField
              label="Who should we contact there?"
              htmlFor="ii-contact"
              required={isNewCustomer}
            >
              <ContactPicker
                contacts={activeContacts}
                value={customerContactId}
                onChange={setCustomerContactId}
                locationChosen={!!customerLocationId}
                disabled={locked}
                onAdd={() => setContactModal({ kind: 'create' })}
                onEdit={(contact) => setContactModal({ kind: 'edit', contact })}
              />
            </FormField>

            {/* Bill-To / Ship-To — capture only. On an existing intake these are
                frozen and are changed through the dedicated address endpoint,
                never by re-submitting this form. */}
            {isCreate && customerId && (
              <AddressSection
                customerId={customerId}
                customer={effectiveCustomer}
                locations={activeLocations}
                billTo={billTo}
                shipTo={shipTo}
                onBillToChange={setBillTo}
                onShipToChange={setShipTo}
                disabled={locked}
              />
            )}
          </>
        )}

        <FormField label="Equipment" htmlFor="ii-equipment" required>
          {/* Locked after creation. The update payload has never carried
              equipmentId, so before this change the picker accepted a new
              machine and silently discarded it on save. Which machine an
              intake is for is decided once, at receipt. */}
          {initial ? (
            <Input id="ii-equipment" value={initial.equipmentLabel} readOnly disabled />
          ) : (
            <EquipmentSearchCombobox
              value={equipmentId}
              onSelect={(r) => {
                setEquipmentId(r?.id ?? '');
                setSelectedEquipmentModelId(r?.equipmentModelId ?? null);
              }}
              disabled={locked}
            />
          )}
        </FormField>

        {isCreate && (
          <FormField label="Ownership Type" htmlFor="ii-ownership">
            <select
              id="ii-ownership"
              value={ownershipTypeAtIntake}
              onChange={(e) => setOwnershipTypeAtIntake(e.target.value as IntakeOwnershipType | '')}
              disabled={locked}
              className={SELECT_CLASS}
            >
              <option value="">— Auto-detect from equipment —</option>
              {OWNERSHIP_TYPES.map((t) => (
                <option key={t} value={t}>
                  {OWNERSHIP_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              Snapshot — cannot be changed after creation.
            </p>
          </FormField>
        )}
      </div>

      <FormField label="Complaint Description" htmlFor="ii-complaint" required>
        <Textarea
          id="ii-complaint"
          value={complaintDescription}
          onChange={(e) => setComplaintDescription(e.target.value)}
          placeholder="Describe the customer's complaint…"
          rows={3}
          required
          disabled={locked}
        />
      </FormField>

      {/* STEP 4 — legacy free text. Shown ONLY when an existing intake already
          carries one, and never writable: the structured checklist below is the
          authoritative record. Existing values are displayed rather than hidden
          because they are real observations somebody made; they are simply not
          migrated, rewritten or deleted. */}
      {conditionOnReceipt && (
        <FormField label="Condition on Receipt (legacy)" htmlFor="ii-condition">
          <Input id="ii-condition" value={conditionOnReceipt} readOnly disabled />
          <p className="mt-1 text-xs text-muted-foreground">
            Recorded before the condition checklist existed. Read-only — use the checklist below.
          </p>
        </FormField>
      )}

      {/* The SAME checklist the Workspace captures — one component, one set of
          items, one order. All six render whether or not anything was recorded,
          so a bulk-created intake can be completed here instead of being stuck
          without condition data forever. */}
      <FormField label="Condition Checklist" htmlFor="ii-condition-checklist">
        <ReceivedConditionChecklist
          value={receivedCondition}
          onChange={setReceivedCondition}
          disabled={locked}
        />
      </FormField>

      {/* ── Priority — writes to the INSPECTION ────────────────────────────────
          Rendered as a control only for holders of inspection:update; everyone
          else sees the value as a read-only badge. A dropdown that always
          returns 403 is worse than no dropdown. */}
      {!isCreate && canReadInspection && (
        <FormField label="Inspection Priority" htmlFor="ii-priority">
          {canUpdateInspection ? (
            <>
              <select
                id="ii-priority"
                value={priorityId}
                onChange={(e) => setPriorityId(e.target.value)}
                disabled={locked || !inspection}
                className={SELECT_CLASS}
              >
                <option value="">— No priority —</option>
                {(priorities ?? []).map((p: { id: string; name: string }) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {inspection
                  ? `Saved to inspection ${inspection.inspectionNumber}, not to this intake.`
                  : 'No inspection has been raised for this intake yet.'}
              </p>
            </>
          ) : (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {inspection?.priorityName ?? '—'}
            </span>
          )}
        </FormField>
      )}

      <FormField
        label={accessoryRows.length > 0 ? `Accessories (${accessoryRows.length})` : 'Accessories'}
        htmlFor="ii-accessories"
      >
        <AccessoryChecklist rows={accessoryRows} onChange={setAccessoryRows} disabled={locked} />
        {isCreate && equipmentId && !selectedEquipmentModelId && (
          <p className="mt-1.5 text-xs text-muted-foreground">
            This equipment has no model — add accessories manually.
          </p>
        )}
      </FormField>

      <FormField label={`Equipment Photos (max ${MAX_PHOTOS})`} htmlFor="ii-photos">
        <PhotoUploadSection
          existingUrls={initial?.photoUrls ?? []}
          removedIndices={photoRemovedIndices}
          pendingEntries={pendingPhotoEntries}
          onRemoveExisting={(i) => setPhotoRemovedIndices((prev) => [...prev, i])}
          onAddFiles={addPhotos}
          onRemovePending={removePendingPhoto}
          disabled={locked}
        />
      </FormField>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Shipments {shipmentStates.length > 0 ? `(${shipmentStates.length})` : ''}
          </p>
          {!saving && (
            <button
              type="button"
              onClick={addShipment}
              className="flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
              Add Shipment
            </button>
          )}
        </div>
        {shipmentStates.length === 0 ? (
          <p className="text-xs text-muted-foreground">No shipments recorded.</p>
        ) : (
          <div className="space-y-3">
            {shipmentStates.map((state, i) => (
              <ShipmentCard
                key={i}
                index={i}
                state={state}
                onChange={(s) => updateShipment(i, s)}
                onRemove={() => removeShipment(i)}
                disabled={locked}
              />
            ))}
          </div>
        )}
      </div>

      <FormField label="Assign Engineer" htmlFor="ii-engineer">
        <EngineerSelect
          value={assignedEngineerId}
          onChange={setAssignedEngineerId}
          disabled={locked}
        />
      </FormField>

      <FormField label="Remarks" htmlFor="ii-remarks">
        <Textarea
          id="ii-remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Any additional remarks…"
          rows={2}
          disabled={locked}
        />
      </FormField>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={locked}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving || !equipmentId}>
          {saving ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>

      {/* Inline creation (Phase 1) — customer, location, contact. */}
      <CustomerFormModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        editTarget={null}
        onSaved={() => setCustomerModalOpen(false)}
        onCreated={(customer) => handleCustomerCreated(customer)}
      />
      {customerId && (
        <>
          {locationModal && (
            <AddressFormModal
              open
              customerId={customerId}
              mode={locationModal}
              onClose={() => setLocationModal(null)}
              onSaved={(selection) => {
                if (locationModal.kind === 'create' && selection.locationId) {
                  handleLocationCreated(selection.locationId);
                }
              }}
            />
          )}

          <AddContactPrompt
            open={!!promptContactFor}
            locationName={
              activeLocations.find((l) => l.id === promptContactFor)?.name ?? 'this location'
            }
            onSkip={() => setPromptContactFor(null)}
            onAddContact={() => {
              setPromptContactFor(null);
              setContactModal({ kind: 'create' });
            }}
          />

          {contactModal && customerLocationId && (
            <ContactFormModal
              open
              customerId={customerId}
              locationId={customerLocationId}
              locationName={selectedLocation?.name ?? 'this location'}
              mode={contactModal}
              onClose={() => setContactModal(null)}
              onSaved={(contact) => handleContactCreated(contact.id)}
            />
          )}
        </>
      )}
      {/* Suggestion only. Every dismissal path — Escape, backdrop, Keep
          current — leaves both snapshots exactly as they were. */}
      <ReceivedLocationChangePrompt
        open={!!locationPrompt}
        currentShipToLabel={initial?.currentShipToLabel ?? '—'}
        suggestedLocationLabel={
          activeLocations.find((l) => l.id === locationPrompt?.locationId)?.name ?? '—'
        }
        saving={shipToUpdate.isPending}
        onKeepCurrent={() => setLocationPrompt(null)}
        onUseSuggested={() => {
          if (!locationPrompt || !initial?.intakeId) return;
          // The DEDICATED address endpoint, carrying Ship-To alone. Addresses
          // never travel through PATCH /equipment-intake/:id.
          shipToUpdate.mutate({
            intakeId: initial.intakeId,
            locationId: locationPrompt.locationId,
          });
        }}
      />
    </form>
  );
}
